import { NAV_PAGES } from '@/config/navPages'
import { pageKeyFromPath } from '@/utils/pagePermissions'

const CONTAINER_WRAPPER_SELECTORS = '[data-bk-container], section, .je-card'
const NESTED_CONTAINER_SELECTORS = '[data-bk-container], section, .je-card'
const OWN_HEADER_HEADING_SELECTORS =
    ':scope > h2, :scope > h3, :scope > .container-title, :scope > header h2, :scope > header h3, :scope > header .container-title, :scope > .je-card__title'
const LEGACY_HEADER_TITLE_SELECTORS = '.container-title, .je-card__title, h2, h3'

const NAV_TABLIST_SELECTORS = '[role="tablist"], [data-bk-nav]'

function collapseWhitespace(text: string): string {
    return text.replace(/\s+/g, ' ').trim()
}

function getTabLabelText(element: Element): string {
    const clone = element.cloneNode(true) as Element
    clone.querySelectorAll('[aria-hidden="true"], button, [role="button"]').forEach((node) => {
        node.remove()
    })
    return collapseWhitespace(clone.textContent ?? '')
}

function getContainerTitleText(element: Element): string {
    const clone = element.cloneNode(true) as Element
    clone.querySelectorAll('[aria-hidden="true"]').forEach((node) => {
        node.remove()
    })
    return collapseWhitespace(clone.textContent ?? '')
}

function isNavigationTablist(tablist: Element): boolean {
    if (tablist.hasAttribute('data-bk-nav')) {
        return true
    }

    if (tablist.getAttribute('role') !== 'tablist') {
        return false
    }

    const selected =
        tablist.querySelector('[aria-selected="true"]') ??
        tablist.querySelector('[aria-current="page"]')

    if (!selected) {
        return false
    }

    if (selected.matches('a[href]')) {
        return true
    }

    if (selected.getAttribute('aria-current') === 'page') {
        return true
    }

    return false
}

function getSelectedTabLabel(tablist: Element): string | null {
    let selected =
        tablist.querySelector('[aria-selected="true"]') ??
        tablist.querySelector('[aria-current="page"]')

    if (!selected && tablist.hasAttribute('data-bk-nav')) {
        selected =
            tablist.querySelector('[aria-pressed="true"]') ??
            Array.from(tablist.querySelectorAll('button')).find((button) => {
                if (button.getAttribute('role') === 'switch') {
                    return false
                }
                const className = button.className
                return (
                    typeof className === 'string' &&
                    className.includes('bg-white') &&
                    className.includes('shadow')
                )
            }) ??
            null
    }

    if (!selected || selected.getAttribute('role') === 'switch') {
        return null
    }

    const text = getTabLabelText(selected)
    return text || null
}

function collectTabLabelsFromTarget(target: Element): string[] {
    const labels: string[] = []
    let node: Element | null = target

    while (node && node !== document.documentElement) {
        const parent: Element | null = node.parentElement
        if (!parent) {
            break
        }

        for (const tablist of parent.querySelectorAll(NAV_TABLIST_SELECTORS)) {
            if (node.contains(tablist)) {
                continue
            }
            if (!isNavigationTablist(tablist)) {
                continue
            }
            const label = getSelectedTabLabel(tablist)
            if (label) {
                labels.unshift(label)
            }
        }

        node = parent
    }

    return labels
}

function getMainContentRoot(): Element | null {
    return document.querySelector('[data-bk-main]')
}

function isNestedContainer(element: Element): boolean {
    return element.matches(NESTED_CONTAINER_SELECTORS)
}

function hasOwnHeaderHeading(element: Element): boolean {
    return element.querySelector(OWN_HEADER_HEADING_SELECTORS) !== null
}

function isContainerWrapper(element: Element): boolean {
    return element.matches(CONTAINER_WRAPPER_SELECTORS) || hasOwnHeaderHeading(element)
}

function findContainerElement(target: Element): Element | null {
    const main = getMainContentRoot()
    let node: Element | null = target

    while (node && node !== document.documentElement) {
        if (isContainerWrapper(node)) {
            if (main && !main.contains(node)) {
                return null
            }
            return node
        }
        node = node.parentElement
    }

    return null
}

function looksLikeSectionTitle(ariaLabel: string): boolean {
    const text = collapseWhitespace(ariaLabel)
    return text.length > 0 && text.length <= 120
}

function findOwnHeaderHeading(container: Element): string | null {
    for (const heading of container.querySelectorAll(OWN_HEADER_HEADING_SELECTORS)) {
        if (heading.tagName === 'H1') {
            continue
        }

        const text = getContainerTitleText(heading)
        if (text) {
            return text
        }
    }

    for (const heading of container.querySelectorAll(LEGACY_HEADER_TITLE_SELECTORS)) {
        if (heading.tagName === 'H1') {
            continue
        }

        let parent: Element | null = heading.parentElement
        let insideNestedContainer = false
        while (parent && parent !== container) {
            if (isNestedContainer(parent)) {
                insideNestedContainer = true
                break
            }
            parent = parent.parentElement
        }

        if (insideNestedContainer) {
            continue
        }

        const text = getContainerTitleText(heading)
        if (text) {
            return text
        }
    }

    return null
}

function findContainerTitle(target: Element): string | null {
    const main = getMainContentRoot()
    if (main && !main.contains(target)) {
        return null
    }

    const container = findContainerElement(target)
    if (!container) {
        return null
    }

    if (main && !main.contains(container)) {
        return null
    }

    const datasetTitle = (container as HTMLElement).dataset.bkContainer
    if (datasetTitle) {
        const text = collapseWhitespace(datasetTitle)
        if (text) {
            return text
        }
    }

    const ariaLabel = container.getAttribute('aria-label')
    if (ariaLabel && looksLikeSectionTitle(ariaLabel)) {
        return collapseWhitespace(ariaLabel)
    }

    return findOwnHeaderHeading(container)
}

function dedupeConsecutiveSegments(segments: string[]): string[] {
    const result: string[] = []
    for (const segment of segments) {
        if (result.length > 0 && result[result.length - 1] === segment) {
            continue
        }
        result.push(segment)
    }
    return result
}

function pageLabelFromPathname(pathname: string): string | null {
    const pageKey = pageKeyFromPath(pathname)
    if (!pageKey) {
        return null
    }
    return NAV_PAGES.find((page) => page.pageKey === pageKey)?.label ?? null
}

export function buildLocationBreadcrumb(args: {
    pathname: string
    search?: string
    target: Element | null
}): string {
    void args.search

    const segments: string[] = ['Bookkeeper']
    const pageName = pageLabelFromPathname(args.pathname)

    if (pageName) {
        segments.push(pageName)
    }

    if (args.target) {
        const tabLabels = collectTabLabelsFromTarget(args.target)
        for (const label of tabLabels) {
            if (label === pageName) {
                continue
            }
            segments.push(label)
        }

        const containerTitle = findContainerTitle(args.target)
        if (
            containerTitle &&
            containerTitle !== pageName &&
            !tabLabels.includes(containerTitle)
        ) {
            segments.push(containerTitle)
        }
    }

    return dedupeConsecutiveSegments(segments).join(' > ')
}
